import { ArrowCounterClockwiseIcon, X } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useResetMeterFrames } from "@/api/media-transmitters/mutations";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

type DetectedMetersBulkActionsProps = {
	sn: string;
	selectedIds: string[];
	onSuccess: () => void;
	onClear: () => void;
};

export function DetectedMetersBulkActions({
	sn,
	selectedIds,
	onSuccess,
	onClear,
}: DetectedMetersBulkActionsProps) {
	const { t } = useTranslation();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const reset = useResetMeterFrames();
	const count = selectedIds.length;

	function handleConfirm() {
		reset.mutate(
			{ sn, serialNumbers: selectedIds },
			{
				onSuccess: () => {
					toast.success(t("mediaTransmitters.resetSuccess"));
					setConfirmOpen(false);
					onSuccess();
				},
				onError: () => {
					toast.error(t("mediaTransmitters.resetError"));
				},
			},
		);
	}

	return (
		<div
			inert={count === 0 ? true : undefined}
			className={`flex h-full w-full items-center justify-between bg-muted px-4 transition-opacity duration-150 ${
				count === 0
					? "pointer-events-none opacity-0"
					: "pointer-events-auto opacity-100"
			}`}
		>
			<div className="flex items-center gap-2">
				<Button
					variant="ghost"
					size="icon"
					className="-ml-2 size-7 shrink-0"
					onClick={onClear}
					aria-label={t("meterBulkActions.clearSelection")}
				>
					<X className="size-3.5" aria-hidden="true" />
				</Button>
				<p className="text-sm text-muted-foreground whitespace-nowrap">
					<span className="font-semibold text-foreground tabular-nums">
						{count}
					</span>{" "}
					{t("meterBulkActions.selected")}
				</p>
			</div>

			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<Button
					variant="ghost"
					className="gap-2"
					disabled={reset.isPending}
					onClick={() => setConfirmOpen(true)}
				>
					<ArrowCounterClockwiseIcon
						className="size-4 shrink-0"
						aria-hidden="true"
					/>
					<span className="hidden sm:inline">
						{t("mediaTransmitters.actionResetSelected")}
					</span>
				</Button>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("mediaTransmitters.resetMetersBulkTitle")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("mediaTransmitters.resetMetersBulkDescription", { count })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("mediaTransmitters.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirm}
							disabled={reset.isPending}
						>
							{t("mediaTransmitters.confirmReset")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
